import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";

// ---------------------------------------------------------------------------
// Cliente del módulo `parking` del API (/parking/*, CC-CONSOLE #1594):
// estacionamientos, cajones, tarifas, validaciones, pensiones, tickets, pagos,
// cortes de caja y solicitudes de factura. El importe de un ticket SIEMPRE lo
// calcula el servidor; aquí sólo se cotiza y se manda el método de pago.
// ---------------------------------------------------------------------------

export type LotType = "covered" | "open" | "multilevel" | "underground";
export type SpaceStatus = "free" | "occupied" | "reserved" | "pension" | "out_of_service";
export type SpaceType = "standard" | "compact" | "disabled" | "ev" | "motorcycle" | "vip";
export type TicketStatus = "open" | "paid" | "exited" | "lost" | "cancelled";
export type PaymentMethod = "cash" | "card_terminal" | "stripe" | "link" | "free";
export type PensionStatus = "active" | "expiring" | "expired" | "scheduled" | "suspended" | "cancelled";
export type InvoiceStatus = "requested" | "ready_to_stamp" | "issued" | "rejected";
export type ValidationType = "free_minutes" | "percent" | "fixed" | "free";

export interface PkLot {
  id: string;
  name: string;
  code?: string;
  site_id?: string | null;
  site_name?: string | null;
  address?: string;
  city?: string;
  type: LotType;
  levels: { id: string; name: string }[];
  entry_point_ids?: string[];
  exit_point_ids?: string[];
  rate_id?: string | null;
  opening_hours?: string;
  photo_url?: string | null;
  status: "active" | "maintenance" | "inactive";
  exit_grace_minutes?: number;
  notes?: string;
}

export interface PkSpace {
  id: string;
  lot_id: string;
  lot_name?: string;
  level: string;
  code: string;
  type: SpaceType;
  status: SpaceStatus;
  ticket_id?: string | null;
  pension_id?: string | null;
  plate?: string | null;
  notes?: string;
}

export interface PkRate {
  id: string;
  name: string;
  description?: string;
  lot_ids: string[];
  vehicle_type: "car" | "motorcycle" | "truck";
  first_period_minutes: number;
  first_period_price: number;
  fraction_minutes: number;
  fraction_price: number;
  grace_minutes: number;
  daily_cap: number;
  night_enabled: boolean;
  night_start: string;
  night_end: string;
  night_price: number;
  lost_ticket_fee: number;
  active: boolean;
  is_default: boolean;
}

export interface PkValidation {
  id: string;
  merchant_name: string;
  code: string;
  type: ValidationType;
  value: number;
  lot_ids: string[];
  active: boolean;
  valid_until?: string | null;
  max_uses?: number;
  uses_count?: number;
  notes?: string;
}

export interface FeeLine {
  concept: "time" | "night_rate" | "grace" | "lost_ticket" | "validation_discount" | "validation_free" | "validation_free_minutes";
  amount: number;
}

export interface FeeAmount {
  minutes: number;
  billable_minutes: number;
  base: number;
  lost_fee: number;
  discount: number;
  total: number;
  night: boolean;
  capped: boolean;
  in_grace: boolean;
  lines: FeeLine[];
  overstay_total?: number;
}

export interface PkTicket {
  id: string;
  folio: string;
  qr_token: string;
  lot_id: string;
  lot_name?: string;
  site_id?: string | null;
  plate?: string | null;
  vehicle_type?: string;
  entry_at: string;
  exit_at?: string | null;
  paid_at?: string | null;
  exit_deadline?: string | null;
  entry_point_id?: string | null;
  exit_point_id?: string | null;
  entry_method?: "lpr" | "manual";
  entry_photo_url?: string | null;
  space_id?: string | null;
  space_code?: string | null;
  level?: string | null;
  rate_id?: string;
  rate_name?: string;
  status: TicketStatus;
  amount?: FeeAmount | null;
  payment_id?: string | null;
  payment_method?: PaymentMethod | null;
  validation_code?: string | null;
  lost?: boolean;
  invoice_request_id?: string | null;
  invoice_status?: InvoiceStatus | null;
  cancel_reason?: string | null;
  /** Código de autofactura impreso en el recibo (folio + código = credencial del portal). */
  invoice_key?: string | null;
  pending_payment_id?: string | null;
}

export interface PkPayment {
  id: string;
  kind: "ticket" | "pension";
  ticket_id?: string | null;
  folio?: string | null;
  pension_id?: string | null;
  holder_name?: string | null;
  months?: number | null;
  lot_id?: string;
  lot_name?: string;
  plate?: string | null;
  method: PaymentMethod;
  amount: number;
  received?: number | null;
  change?: number;
  reference?: string | null;
  status: "paid" | "pending" | "cancelled" | "needs_refund" | "refunded";
  refund_reason?: string | null;
  expires_at?: string | null;
  shift_id?: string | null;
  cashier_name?: string | null;
  checkout_url?: string | null;
  paid_at?: string | null;
  createdAt?: string;
}

export interface PkPension {
  id: string;
  holder_name: string;
  holder_phone?: string;
  holder_email?: string;
  company_name?: string | null;
  plates: string[];
  lot_id: string;
  lot_name?: string;
  space_id?: string | null;
  space_code?: string | null;
  monthly_fee: number;
  valid_from: string;
  valid_until: string;
  credential_type: "card" | "tag" | "plate";
  credential_number?: string;
  status: "active" | "suspended" | "cancelled";
  effective_status: PensionStatus;
  last_payment_at?: string | null;
  notes?: string;
}

export interface ShiftTotals {
  count: number;
  tickets: number;
  pensions: number;
  total: number;
  by_method: Record<PaymentMethod, number>;
  change_given: number;
  expected_cash: number;
}

export interface PkShift {
  id: string;
  number: number;
  lot_id: string;
  lot_name?: string;
  cashier_id: string;
  cashier_name?: string | null;
  opening_float: number;
  status: "open" | "closed";
  opened_at: string;
  closed_at?: string | null;
  running_total?: number;
  running_count?: number;
  totals?: ShiftTotals;
  counted_cash?: number;
  difference?: number;
  close_notes?: string;
}

export interface PkInvoice {
  id: string;
  ticket_id: string;
  folio: string;
  lot_name?: string;
  rfc: string;
  legal_name: string;
  tax_regime: string;
  cfdi_use: string;
  zip: string;
  email: string;
  subtotal: number;
  iva: number;
  total: number;
  status: InvoiceStatus;
  source: "console" | "portal";
  prefactura_number?: string | null;
  cfdi_uuid?: string | null;
  pac_provider?: string | null;
  rejection_reason?: string | null;
  requested_at: string;
  paid_at?: string | null;
  concept?: { product_key: string; unit_key: string; description: string; unit_price: number; subtotal: number; iva: number; total: number };
}

export interface ParkingSummary {
  occupancy: { lot_id: string; name: string; capacity: number; by_status: Record<SpaceStatus, number>; occupancy_pct: number; open_tickets: number }[];
  totals: {
    capacity: number;
    busy: number;
    free: number;
    occupancy_pct: number;
    open_tickets: number;
    lost_tickets: number;
    entries_today: number;
    exits_today: number;
    revenue_today: number;
    payments_today: number;
    avg_stay_minutes: number;
    open_shifts: number;
  };
  revenue: { daily: { day: string; tickets: number; pensions: number }[]; by_method_today: Record<PaymentMethod, number> };
  hourly: { hour: number; entries: number; exits: number }[];
  pensions: Record<PensionStatus, number>;
  invoices: Record<InvoiceStatus, number>;
  recentTickets: PkTicket[];
  lots: PkLot[];
  /** Cobros en línea que llegaron cuando el ticket ya no los esperaba: hay que reembolsar. */
  refunds?: PkPayment[];
}

export interface Quote {
  ticket: PkTicket;
  lot: { id: string; name: string };
  rate: { id: string; name: string };
  amount: FeeAmount;
  overstay: boolean;
  validation: { id: string; merchant_name: string; code: string; type: ValidationType; value: number } | null;
  quoted_at: string;
}

export interface ShiftReport {
  shift: PkShift;
  payments: PkPayment[];
  totals: ShiftTotals;
}

export type PkEntity = "lots" | "spaces" | "rates" | "validations" | "pensions" | "tickets" | "payments" | "shifts" | "invoices";

interface PkEntityMap {
  lots: PkLot;
  spaces: PkSpace;
  rates: PkRate;
  validations: PkValidation;
  pensions: PkPension;
  tickets: PkTicket;
  payments: PkPayment;
  shifts: PkShift;
  invoices: PkInvoice;
}

const BASE = "/parking";
const data = <T,>(res: { data?: { data?: unknown } }) => res.data?.data as T;

export async function listPk<E extends PkEntity>(entity: E, params: Record<string, string | number | undefined> = {}): Promise<PkEntityMap[E][]> {
  const res = await api.get(`${BASE}/${entity}`, { params: { limit: 1000, ...params } });
  return (res.data?.data ?? []) as PkEntityMap[E][];
}

export const parkingApi = {
  summary: async (lotId?: string) => data<ParkingSummary>(await api.get(`${BASE}/summary`, { params: lotId ? { lot_id: lotId } : {} })),
  get: async <E extends PkEntity>(entity: E, id: string) => data<PkEntityMap[E]>(await api.get(`${BASE}/${entity}/${encodeURIComponent(id)}`)),
  create: async <E extends PkEntity>(entity: E, body: Partial<PkEntityMap[E]>) => data<PkEntityMap[E]>(await api.post(`${BASE}/${entity}`, body)),
  update: async <E extends PkEntity>(entity: E, id: string, body: Partial<PkEntityMap[E]>) => data<PkEntityMap[E]>(await api.put(`${BASE}/${entity}/${encodeURIComponent(id)}`, body)),
  remove: async (entity: PkEntity, id: string) => data<{ deleted: boolean }>(await api.delete(`${BASE}/${entity}/${encodeURIComponent(id)}`)),
  quote: async (body: { code?: string; ticket_id?: string; validation_code?: string; lost?: boolean }) => data<Quote>(await api.post(`${BASE}/tickets/quote`, body)),
  entry: async (body: { lot_id: string; plate?: string; vehicle_type?: string; space_id?: string }) =>
    data<{ ticket: PkTicket | null; pension: PkPension | null; granted: boolean; reason: string }>(await api.post(`${BASE}/tickets/entry`, body)),
  pay: async (body: { ticket_id: string; method: PaymentMethod; received?: number; reference?: string; validation_code?: string; lost?: boolean; success_url?: string; cancel_url?: string; replace_pending?: boolean }) =>
    data<{ payment: PkPayment; ticket: PkTicket | null; amount: FeeAmount; url?: string | null }>(await api.post(`${BASE}/tickets/pay`, body)),
  exit: async (body: { ticket_id?: string; code?: string }) => data<{ ticket: PkTicket; granted: boolean }>(await api.post(`${BASE}/tickets/exit`, body)),
  lost: async (id: string) => data<Quote>(await api.post(`${BASE}/tickets/${encodeURIComponent(id)}/lost`)),
  cancel: async (id: string, reason: string) => data<PkTicket>(await api.post(`${BASE}/tickets/${encodeURIComponent(id)}/cancel`, { reason })),
  gateEvent: async (body: { point_id: string; plate?: string; direction?: "entry" | "exit" }) => data<{ granted: boolean; reason: string; ticket?: PkTicket | null }>(await api.post(`${BASE}/gate-events`, body)),
  renewPension: async (id: string, body: { months: number; method: PaymentMethod; received?: number; reference?: string }) =>
    data<{ payment: PkPayment; pension?: PkPension; url?: string | null }>(await api.post(`${BASE}/pensions/${encodeURIComponent(id)}/renew`, body)),
  currentShift: async () => data<PkShift | null>(await api.get(`${BASE}/shifts/current`)),
  openShift: async (body: { lot_id: string; opening_float: number; notes?: string }) => data<PkShift>(await api.post(`${BASE}/shifts/open`, body)),
  closeShift: async (id: string, body: { counted_cash: number; notes?: string }) => data<PkShift>(await api.post(`${BASE}/shifts/${encodeURIComponent(id)}/close`, body)),
  shiftReport: async (id: string) => data<ShiftReport>(await api.get(`${BASE}/shifts/${encodeURIComponent(id)}/report`)),
  requestInvoice: async (body: Record<string, string>) => data<PkInvoice>(await api.post(`${BASE}/invoices/request`, body)),
  approveInvoice: async (id: string) => data<PkInvoice>(await api.post(`${BASE}/invoices/${encodeURIComponent(id)}/approve`)),
  rejectInvoice: async (id: string, reason: string) => data<PkInvoice>(await api.post(`${BASE}/invoices/${encodeURIComponent(id)}/reject`, { reason })),
  stampInvoice: async (id: string) => data<PkInvoice>(await api.post(`${BASE}/invoices/${encodeURIComponent(id)}/stamp`)),
  registerIssued: async (id: string, uuid: string) => data<PkInvoice>(await api.post(`${BASE}/invoices/${encodeURIComponent(id)}/issued`, { uuid })),
  pacStatus: async () => data<{ provider: string; enabled: boolean }>(await api.get(`${BASE}/pac`)),
};

// --- Hooks -----------------------------------------------------------------

export const pkKeys = {
  all: ["parking"] as const,
  list: (entity: PkEntity, params?: Record<string, unknown>) => ["parking", entity, params ?? {}] as const,
  summary: (lotId?: string) => ["parking", "summary", lotId ?? "all"] as const,
  shift: ["parking", "shift", "current"] as const,
};

export function usePkList<E extends PkEntity>(entity: E, params?: Record<string, string | number | undefined>, options: { refetchInterval?: number; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: pkKeys.list(entity, params),
    queryFn: () => listPk(entity, params),
    staleTime: 15_000,
    refetchInterval: options.refetchInterval,
    enabled: options.enabled ?? true,
  });
}

export function usePkSummary(lotId?: string, options: { refetchInterval?: number } = {}) {
  return useQuery({ queryKey: pkKeys.summary(lotId), queryFn: () => parkingApi.summary(lotId), staleTime: 15_000, refetchInterval: options.refetchInterval });
}

export function useCurrentShift() {
  return useQuery({ queryKey: pkKeys.shift, queryFn: parkingApi.currentShift, staleTime: 10_000 });
}

/** Mutación que invalida todo el módulo (y el control de acceso) al terminar. */
export function usePkMutation<A, R>(fn: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pkKeys.all });
      qc.invalidateQueries({ queryKey: ["access-control"] });
    },
  });
}

/** Error del API: código estable (`parkingLotFull`…), mensaje ya traducido y campos. */
export function apiError(err: unknown): { code: string | null; message: string | null; fields: string[]; status: number | null } {
  const res = (err as { response?: { status?: number; data?: { meta?: { message?: string; code?: string; details?: { fields?: string[] } } } } })?.response;
  const meta = res?.data?.meta;
  const fields = meta?.details?.fields;
  return { code: meta?.code ?? null, message: meta?.message ?? null, fields: Array.isArray(fields) ? fields : [], status: res?.status ?? null };
}
